import React from "react";
import { useAtom } from "jotai";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import styles from "./App.module.scss";
import Footer from "./molecules/footer/Footer";
import ScrollToTop from "./common/utility/ScrollToTop";
import SideMenu from "./organism/side-menu/SideMenu";
import TypeDiagnosisTop from "./pages/type-diagnosis-top/TypeDiagnosisTop";
import TypeDiagnosisContents from "./pages/type-diagnosis-contents/TypeDiagnosisContents";
import TypeDiagnosisResult from "./pages/type-diagnosis-result/TypeDiagnosisResult";
import ErrorHandler from "./pages/error-handler/ErrorHandler";
import Loading, { useLoading } from "./pages/loading/Loading";
import Login from "./pages/login/Login";
import PasswordResetRequest from "./pages/password-reset-request/PasswordResetRequest";
import PasswordResetMail from "./pages/password-reset-mail/PasswordResetMail";
import Privacy from "./pages/privacy/Privacy";
import RegisterTempUser from "./pages/register-temp-user/RegisterTempUser";
import Serviceterm from "./pages/serviceterm/Serviceterm";
import {
  favoriteModalStateAtom,
  modalStateAtom,
} from "./state/modal/ModalAtom";
import { sideMenuAtom } from "./state/side-menu/SideMenuAtom";
import QuizDetail from "./pages/quiz-detail/QuizDetail";
import QuizAnswer from "./pages/quiz-answer/QuizAnswer";
import RegisterResult from "./pages/register-result/RegisterResult";
import RegisterUser from "./pages/register-user/RegisterUser";
import RegisterComplete from "./pages/register-complete/RegisterComplete";
import RegisterRecreate from "./pages/password-recreate/PasswordRecreate";
import DiagnosisList from "./pages/diagnosis-list/DiagnosisList";
import TokenExpire from "./pages/token-expire/TokenExpire";
import Top from "./pages/top/Top";
import Quiz from "./pages/quiz/Quiz";
import Point from "./pages/point/Point";
import PointExchange from "./pages/point-exchange/PointExchange";
import PointExchangeResult from "./pages/point-exchange-result/PointExchangeResult";
import PointDetail from "./pages/point-detail/PointDetail";
import PointGift from "./pages/point-gift/PointGift";

import BadgeList from "./pages/badge-list/BadgeList";
import DiagnosisExternal from "./pages/diagnosis-external/DiagnosisExternal";
import PrepareTop from "./pages/prepare-top/PrepareTop";
import PrepareDiagnosis from "./pages/prepare-diagnosis/PrepareDiagnosis";
import PrepareRegister from "./pages/prepare-register/PrepareRegister";
import PrepareResult from "./pages/prepare-result/PrepareResult";

import CompareTop from "./pages/compare-top/CompareTop";
import CompareInput from "./pages/compare-input/CompareInput";
import CompareResult from "./pages/compare-result/CompareResult";
import MisaTop from "./pages/misa-top/MisaTop";

const App = () => {
  const [state] = useAtom(sideMenuAtom);
  const [modalState] = useAtom(modalStateAtom);
  const [favoriteModalState] = useAtom(favoriteModalStateAtom);
  const loading = useLoading();
  
  const containerStyle = () => {
    if (state.isShow) {
      return {
        background: "grey",
      };
    }
    if (modalState.isOpen || favoriteModalState.isOpen) {
      return {
        height: "100vh",
        overflow: "hidden",
      };
    }
    return {};
  };

  return (
    <div className={styles.appContainer} style={containerStyle()}>
      <BrowserRouter basename={"/swan"}>
        <ScrollToTop />
        <ErrorHandler />
        <Loading />
        {state.isShow ? (
          <SideMenu></SideMenu>
        ) : (
          <>
            <Routes>
              <Route index element={<Navigate replace to={"/familynavi"} />} />

              {/* --- 備えナビパス --- */}
              <Route path="/familynavi" element={<PrepareTop />} />
              <Route path="/familynavi/diagnosis" element={<PrepareDiagnosis />} />
              <Route path="/familynavi/register" element={<PrepareRegister />} />
              <Route path="/familynavi/result" element={<PrepareResult />} />

              {/* --- 比較ナビパス --- */}
              <Route path="/comparenavi" element={<CompareTop />} />
              <Route path="/comparenavi/input" element={<CompareInput />} />
              <Route path="/comparenavi/result" element={<CompareResult />} />

              {/* --- 9タイプ診断パス（ログイン後） --- */}
              <Route path="/type9-diagnosis" element={<TypeDiagnosisTop />} />
              <Route path="/type9-diagnosis-contents" element={<TypeDiagnosisContents />} />
              <Route path="/type9-diagnosis-result" element={<TypeDiagnosisResult/>} />

              {/* --- トップページ --- */}
              <Route path="/top" element={<Top />} />


              {/* --- MISAトップページ --- */}
              <Route path="/misa-top" element={<MisaTop />} />

              {/* --- 診断一覧・外部診断 --- */}
              <Route path="/diagnosis-list" element={<DiagnosisList />} />
              <Route path="/diagnosis-external" element={<DiagnosisExternal />} />

              {/* --- クイズ関連 --- */}
              <Route path="/quiz" element={<Quiz />} />
              <Route path="/quiz-contents" element={<QuizDetail />} />
              <Route path="/quiz-answer" element={<QuizAnswer />} />
              <Route path="/badge-list" element={<BadgeList />} />

              {/* --- ポイント関連 --- */}
              <Route path="/point" element={<Point />} />
              <Route path="/poiint-gift" element={<PointGift />} />
              <Route path="/point-gift" element={<PointGift />} />
              <Route path="/point-exchange/:giftId" element={<PointExchange />} />
              <Route path="/point-result" element={<PointExchangeResult />} />
              <Route path="/point-detail" element={<PointDetail />} />
              
              {/* --- アカウント：ログイン・パスワード --- */}
              <Route path="/login" element={<Login />} />
              <Route path="/password/reset-request" element={<PasswordResetRequest />} />
              <Route path="/password/reset-mail" element={<PasswordResetMail />} />
              <Route path="/password/recreate" element={<RegisterRecreate />} />

              {/* --- アカウント：新規登録 --- */}
              <Route path="/registration" element={<RegisterTempUser />} />
              <Route path="/register-result" element={<RegisterResult />} />
              <Route path="/register-user" element={<RegisterUser />} />
              <Route path="/register-complete" element={<RegisterComplete />} />
              <Route path="/token-expire" element={<TokenExpire />} />

              {/* --- 規約・ポリシー --- */}
              <Route path="/serviceterm" element={<Serviceterm />} />
              <Route path="/privacy" element={<Privacy />} />

              {/* --- その他（未定義パス） --- */}
              <Route path="*" element={<Navigate replace to={"/familynavi"} />} />
              <Route
                path="*"
                element={<Navigate replace to={"/familynavi"} />}
              />
            </Routes>
          </>
        )}
        <Footer />
      </BrowserRouter>
    </div>
  );
};

export default App;
